/**
 * ArchetypePickerDialog
 *
 * Replaces the plain <select> for archetype choosing.
 * Opens a full dialog with:
 *  - Search bar
 *  - "Most Common" sections pinned at the top (Hero/Heroine + Villain/Foil)
 *  - All other groups below
 *  - Hover tooltip showing the full trait definition + examples
 */
import React, { useState, useMemo } from 'react';
import { ARCHETYPES, ARCHETYPE_GROUPS } from '@/lib/archetypes';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, ChevronDown, Info, Wand2, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

const MOST_COMMON_GROUPS = [
  'Most Common — Hero / Heroine',
  'Most Common — Villain / Foil',
];

function ArchetypeCard({ arc, onSelect }) {
  const isMostCommon = arc.group.startsWith('Most Common');
  return (
    <TooltipProvider delayDuration={120}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => onSelect(arc)}
            data-testid={`archetype-card-${arc.id}`}
            className={cn(
              'group flex w-full items-start gap-2 rounded-md border px-3 py-2 text-left transition-all',
              'hover:border-primary/60 hover:bg-primary/5 hover:shadow-sm',
              isMostCommon
                ? 'border-panel-border/80 bg-card'
                : 'border-panel-border/50 bg-card/50',
            )}
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <span className={cn(
                  'truncate text-[14px] font-medium',
                  isMostCommon ? 'text-foreground' : 'text-foreground/90',
                )}>
                  {arc.name}
                </span>
                {arc.traits && (
                  <Info className="h-3 w-3 shrink-0 text-muted-foreground/60 group-hover:text-primary/70" />
                )}
              </div>
              <p className="mt-0.5 line-clamp-2 text-[13px] text-muted-foreground leading-relaxed">
                {arc.blurb}
              </p>
              {arc.example && (
                <p className="mt-0.5 truncate text-[13px] text-muted-foreground/60 italic">
                  {arc.example}
                </p>
              )}
            </div>
          </button>
        </TooltipTrigger>
        {(arc.traits || arc.blurb) && (
          <TooltipContent
            side="right"
            align="start"
            className="max-w-[280px] space-y-1.5 p-3 text-[13.5px] leading-relaxed"
            data-testid={`archetype-tooltip-${arc.id}`}
          >
            <p className="font-semibold text-foreground">{arc.name}</p>
            {arc.traits && (
              <p className="text-muted-foreground">{arc.traits}</p>
            )}
            {arc.example && (
              <p className="text-[13px] text-muted-foreground/70 italic border-t border-border/40 pt-1.5">
                Examples: {arc.example}
              </p>
            )}
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
}

function GroupSection({ group, archetypes, onSelect, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const isMostCommon = group.startsWith('Most Common');
  const label = isMostCommon ? group.replace('Most Common — ', '') : group;

  return (
    <div className="mb-3">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className={cn(
          'flex w-full items-center gap-2 rounded-sm px-1 py-1 text-left transition-colors hover:bg-secondary/40',
        )}
      >
        {isMostCommon && (
          <span className="rounded-sm bg-primary/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-primary">
            Most Common
          </span>
        )}
        <span className={cn(
          'text-[13px] font-semibold uppercase tracking-wider',
          isMostCommon ? 'text-primary/80' : 'text-muted-foreground',
        )}>
          {label}
        </span>
        <span className="ml-1 text-[13px] text-muted-foreground/60">
          ({archetypes.length})
        </span>
        <ChevronDown className={cn(
          'ml-auto h-3.5 w-3.5 text-muted-foreground/60 transition-transform',
          open && 'rotate-180',
        )} />
      </button>
      {open && (
        <div className={cn(
          'mt-1.5 grid gap-1.5',
          isMostCommon ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2',
        )}>
          {archetypes.map(arc => (
            <ArchetypeCard key={arc.id} arc={arc} onSelect={onSelect} />
          ))}
        </div>
      )}
    </div>
  );
}

export function ArchetypePickerDialog({
  open, onOpenChange, currentArchetype, onSelect,
  onAiSuggest, suggesting = false,
}) {
  const [query, setQuery] = useState('');

  const grouped = useMemo(() => {
    const q = query.toLowerCase().trim();
    const filtered = q
      ? ARCHETYPES.filter(a =>
          a.name.toLowerCase().includes(q) ||
          a.blurb.toLowerCase().includes(q) ||
          (a.traits || '').toLowerCase().includes(q) ||
          (a.example || '').toLowerCase().includes(q),
        )
      : ARCHETYPES;

    return ARCHETYPE_GROUPS.map(g => ({
      group: g,
      archetypes: filtered.filter(a => a.group === g),
    })).filter(g => g.archetypes.length > 0);
  }, [query]);

  const handleSelect = (arc) => {
    onSelect(arc);
    onOpenChange(false);
    setQuery('');
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setQuery(''); }}>
      <DialogContent
        className="flex max-h-[88vh] max-w-2xl flex-col"
        data-testid="archetype-picker-dialog"
      >
        <DialogHeader className="shrink-0 pb-1">
          <DialogTitle className="text-sm">Choose Archetype</DialogTitle>
          {currentArchetype && (
            <p className="text-[13px] text-muted-foreground">
              Current: <span className="font-medium text-foreground">{currentArchetype}</span>
            </p>
          )}
        </DialogHeader>

        {/* AI suggest banner */}
        {onAiSuggest && (
          <div className="shrink-0 rounded-md border border-primary/20 bg-primary/5 px-3 py-2 flex items-center justify-between gap-3">
            <p className="text-[13.5px] text-muted-foreground leading-snug">
              Let Claude pick the best archetype based on the character's description and the book's storyline.
            </p>
            <Button
              size="sm"
              variant="outline"
              className="shrink-0 h-7 gap-1.5 text-[13.5px] border-primary/40 text-primary hover:bg-primary/10"
              onClick={() => { onAiSuggest(); onOpenChange(false); }}
              disabled={suggesting}
              data-testid="archetype-picker-ai-suggest"
            >
              {suggesting
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <Wand2 className="h-3.5 w-3.5" />}
              {suggesting ? 'Thinking…' : 'Suggest'}
            </Button>
          </div>
        )}

        {/* Search */}
        <div className="relative shrink-0">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            autoFocus
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search archetypes… (name, trait, example)"
            className="h-8 pl-8 text-[13.5px]"
            data-testid="archetype-picker-search"
          />
        </div>

        {/* Scroll area */}
        <div className="flex-1 overflow-y-auto scroll-thin py-1 pr-1">
          {grouped.length === 0 ? (
            <p className="py-8 text-center text-[13.5px] text-muted-foreground">
              No archetypes match "{query}"
            </p>
          ) : (
            grouped.map(({ group, archetypes }) => (
              <GroupSection
                key={group}
                group={group}
                archetypes={archetypes}
                onSelect={handleSelect}
                defaultOpen={group.startsWith('Most Common') || !!query}
              />
            ))
          )}
        </div>

        <p className="shrink-0 border-t border-panel-border pt-2 text-[13px] text-muted-foreground">
          Hover any archetype for its full definition · {ARCHETYPES.length} archetypes across {ARCHETYPE_GROUPS.length} groups
        </p>
      </DialogContent>
    </Dialog>
  );
}
